import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { IconoirNavArrowRight } from '@ey-xd/motif-icon';
import { MotifIconModule } from '@ey-xd/ng-motif';

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [RouterModule, MotifIconModule],
  templateUrl: './breadcrumb.component.html',
  styleUrl: './breadcrumb.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbComponent implements OnInit {
  #route = inject(ActivatedRoute);
  #pathFromRoot = '';

  readonly separatorIcon = IconoirNavArrowRight;
  readonly breadcrumbs = signal<BreadcrumbElement[]>([]);

  public ngOnInit(): void {
    this.#pathFromRoot = this.getPathFromRoot(this.#route);

    this.buildBreadcrumbs();
  }

  private buildBreadcrumbs(): void {
    let route: ActivatedRoute | null = this.#route;
    const breadcrumbs: ActivatedRoute[] = [];

    while (route) {
      if (route.snapshot.url.length > 0 && route.snapshot.data['crumb']) {
        breadcrumbs.splice(0, 0, route);
      }
      route = route.parent;
    }

    this.breadcrumbs.set([
      new BreadcrumbElement('Home', '/'),
      ...breadcrumbs.map(route => this.mapToBreadcrumbElement(route)),
    ]);
  }

  private mapToBreadcrumbElement(route: ActivatedRoute): BreadcrumbElement {
    const label = this.getLabel(route);

    const pathFromRoot = this.getPathFromRoot(route);

    return new BreadcrumbElement(label, pathFromRoot, this.#pathFromRoot === pathFromRoot);
  }

  private getPathFromRoot(route: ActivatedRoute): string {
    return (
      '/' +
      route.pathFromRoot
        .map(route => route.snapshot.url)
        .flat()
        .map(segment => segment.path)
        .join('/')
    );
  }

  private getLabel(route: ActivatedRoute): string {
    const crumb = route.snapshot.data['crumb'];

    if (typeof crumb === 'string') {
      return crumb;
    } else if (typeof crumb === 'function') {
      return crumb(route.snapshot.params);
    }

    return '';
  }
}

class BreadcrumbElement {
  public constructor(
    public label: string,
    public url: string,
    public isCurrent = false
  ) {}
}
